import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react'

import { StructureObjectDto } from '@/endpoints/models'

import { reducer } from './reducer'
import {
	ActionTypeEnum,
	DetailTabContextState,
	DetailTabContextType,
} from './types'

const DetailTabContext = createContext<DetailTabContextType | undefined>(
	undefined,
)

export const useDetailTabContext = () =>
	useContext(DetailTabContext) as DetailTabContextType

type Props = {
	children: React.ReactNode
	initialState: DetailTabContextState
}

export const DetailTabContextProvider = ({
	children,
	initialState,
}: Props) => {
	const [state, dispatch] = useReducer(reducer, initialState)

	const context = useMemo<DetailTabContextType>(
		() => ({
			state,
			dispatch,
		}),
		[state, dispatch],
	)

	useEffect(() => {
		const isV2View = initialState.node.type === StructureObjectDto.TypeEnum.VIEW

		dispatch({
			type: ActionTypeEnum.DETAIL_TAB_UPDATE,
			update: {
				node: initialState.node,
				systemNodeId: initialState.systemNodeId,
				editModeProp: initialState.editModeProp,
				selectedTab: initialState.selectedTab,
				editMode: initialState.version ? false : initialState.editModeProp,
				// V2 history is synced from DetailHeaderWithHistory; do not overwrite here.
				...(isV2View
					? {}
					: {
							version: initialState.version,
							versionActive: initialState.versionActive,
							versionMax: initialState.versionMax,
						}),
			},
		})
	}, [initialState])

	return (
		<DetailTabContext.Provider value={context}>
			{children}
		</DetailTabContext.Provider>
	)
}

DetailTabContext.displayName = 'DetailTabContext'
